import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { ref as dbRef, onValue, set, get } from "firebase/database";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { database, storage } from "../../../firebaseConfig";

interface SessionPhoto {
  id: string;
  url: string;
  title: string;
  description: string;
  photoNumber: number;
  timestamp: number;
}

interface CameraSession {
  status: "active" | "completed" | "expired";
  unitName: string;
  unitPlate: string;
  createdAt: number;
  expiresAt: number;
}

const compressImage = (
  file: File,
  maxWidth = 1200,
  quality = 0.82,
): Promise<Blob> => {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob!), "image/jpeg", quality);
      };
    };
    reader.readAsDataURL(file);
  });
};

export default function CameraSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<CameraSession | null>(null);
  const [photos, setPhotos] = useState<SessionPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!sessionId) {
      setError("Sesión inválida");
      setLoading(false);
      return;
    }

    const sessionRefPath = dbRef(database, `cameraSession/${sessionId}`);

    get(sessionRefPath)
      .then((snap) => {
        if (!snap.exists()) {
          setError("Sesión no encontrada o expirada");
          setLoading(false);
          return;
        }
        const data = snap.val() as CameraSession;
        if (data.status === "completed" || Date.now() > data.expiresAt) {
          setCompleted(true);
          setSession(data);
          setLoading(false);
          return;
        }
        setSession(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Error al cargar la sesión");
        setLoading(false);
      });

    const photosRefPath = dbRef(database, `cameraSession/${sessionId}/photos`);
    const unsubscribe = onValue(photosRefPath, (snap) => {
      if (snap.exists()) {
        const data = snap.val() as Record<string, SessionPhoto>;
        const arr = Object.values(data).sort(
          (a, b) => a.photoNumber - b.photoNumber,
        );
        setPhotos(arr);
      }
    });

    return () => unsubscribe();
  }, [sessionId]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !sessionId) return;

    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const photoId = crypto.randomUUID();
      const photoNumber = photos.length + 1;
      const path = `cameraSession/${sessionId}/${photoId}.jpg`;
      const sRef = storageRef(storage, path);

      await uploadBytes(sRef, compressed, { contentType: "image/jpeg" });
      const url = await getDownloadURL(sRef);

      const photoData: SessionPhoto = {
        id: photoId,
        url,
        title: "",
        description: "",
        photoNumber,
        timestamp: Date.now(),
      };

      await set(
        dbRef(database, `cameraSession/${sessionId}/photos/${photoId}`),
        photoData,
      );
    } catch (err) {
      console.error("Error uploading photo:", err);
      alert("Error al subir la foto. Intenta de nuevo.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUpdatePhoto = async (
    photoId: string,
    field: "title" | "description",
    value: string,
  ) => {
    if (!sessionId) return;
    setPhotos((prev) =>
      prev.map((p) => (p.id === photoId ? { ...p, [field]: value } : p)),
    );
    await set(
      dbRef(database, `cameraSession/${sessionId}/photos/${photoId}/${field}`),
      value,
    );
  };

  const handleComplete = async () => {
    if (!sessionId) return;
    await set(
      dbRef(database, `cameraSession/${sessionId}/status`),
      "completed",
    );
    setCompleted(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Cargando sesión...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-3xl text-red-400" />
          </div>
          <h2 className="text-white text-lg font-bold mb-2">Sesión inválida</h2>
          <p className="text-gray-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-checkbox-circle-line text-4xl text-green-400" />
          </div>
          <h2 className="text-white text-xl font-bold mb-2">
            Sesión completada
          </h2>
          <p className="text-gray-400 text-sm">
            Se enviaron {photos.length} foto{photos.length !== 1 ? "s" : ""} a
            la inspección.
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Puedes cerrar esta página.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white pb-8">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-truck-line text-gray-900 text-lg" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm truncate">
              {session?.unitName || "Unidad"}
            </p>
            <p className="text-gray-400 text-xs">
              {session?.unitPlate || ""} · Sesión de cámara
            </p>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-400 text-xs font-medium">Activa</span>
          </div>
        </div>
      </div>

      <div className="px-4 pt-5 space-y-5">
        {/* Capture button */}
        <div className="bg-gray-900 rounded-2xl border border-gray-800 p-5 text-center">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <i
              className={`ri-camera-line text-3xl ${uploading ? "text-gray-500" : "text-green-400"}`}
            />
          </div>
          <p className="text-white font-semibold mb-1">
            {uploading ? "Subiendo foto..." : "Capturar foto"}
          </p>
          <p className="text-gray-400 text-xs mb-4">
            {photos.length} foto{photos.length !== 1 ? "s" : ""} capturadas
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileChange}
            disabled={uploading}
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full py-3 rounded-xl font-semibold text-gray-900 transition-all
              bg-green-400 hover:bg-green-300 disabled:bg-gray-700 disabled:text-gray-500"
          >
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                Subiendo...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-camera-2-line" />
                Tomar Foto
              </span>
            )}
          </button>
        </div>

        {/* Photos list */}
        {photos.length > 0 && (
          <div className="space-y-3">
            <p className="text-gray-300 text-sm font-semibold px-1">
              Fotos capturadas ({photos.length})
            </p>
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden"
              >
                <img
                  src={photo.url}
                  alt={`Foto ${photo.photoNumber}`}
                  className="w-full h-48 object-cover"
                />
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      Foto #{photo.photoNumber} ·{" "}
                      {new Date(photo.timestamp).toLocaleTimeString("es-ES", {
                        timeStyle: "short",
                      })}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder="Título (opcional)"
                    value={photo.title}
                    onChange={(e) =>
                      handleUpdatePhoto(photo.id, "title", e.target.value)
                    }
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <textarea
                    placeholder="Observaciones (opcional)"
                    value={photo.description}
                    rows={2}
                    onChange={(e) =>
                      handleUpdatePhoto(photo.id, "description", e.target.value)
                    }
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Complete button */}
        {photos.length > 0 && (
          <button
            onClick={handleComplete}
            className="w-full py-3.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-500 transition-colors flex items-center justify-center gap-2"
          >
            <i className="ri-send-plane-line" />
            Enviar {photos.length} foto{photos.length !== 1 ? "s" : ""} a la
            inspección
          </button>
        )}
      </div>
    </div>
  );
}
